<template>
  <RevButton
    class="min-w-[58px]"
    :class="{ voted }"
    data-test="review-vote-button"
    size="small"
    variant="secondary"
    @click="voteStatus === 'up' ? vote('unvoted') : vote('up')"
  >
    <div class="flex items-center gap-2">
      <span
        :class="{
          textAnimationIn: voteStatus === 'up',
          textAnimationOut: voteStatus === 'unvoted',
        }"
        >{{ displayedVotesCount }}</span
      >
      <IconThumbsUpOutlined
        :class="{
          iconAnimationIn: voteStatus === 'up',
          iconAnimationOut: voteStatus === 'unvoted',
        }"
        size="16"
      />
    </div>
  </RevButton>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { IconThumbsUpOutlined } from '@ds/icons/IconThumbsUpOutlined'
import { type StorageLike, useStorage } from '@vueuse/core'

import { VOTED_REVIEWS_STORAGE_INJECTION_KEY } from './voted-reviews-storage'

const voteStatus = ref<'up' | 'unvoted' | null>(null)
const voted = computed(() => voteStatus.value === 'up')

const { reviewId, votesCount = 0 } = defineProps<{
  reviewId: string
  votesCount: number
}>()

const $httpFetch = useHttpFetch()

let hadPreviouslyVotedForReview = false

const displayedVotesCount = computed(() => {
  if (!hadPreviouslyVotedForReview) {
    return voteStatus.value === 'up' ? votesCount + 1 : votesCount
  }

  return voteStatus.value === 'unvoted' ? votesCount - 1 : votesCount
})

const { openErrorToast } = useTheToast()

/**
 * The inject is only used in the test ReviewDisplay.nuxt.spec.ts
 * In the application, no storage is provided. The storage will be nothing during SSR and localStorage on the browser.
 * This is handled directly by vueuse/useStorage. See https://github.com/vueuse/vueuse/blob/main/packages/core/useStorage/index.ts
 */
const storage = inject<StorageLike>(VOTED_REVIEWS_STORAGE_INJECTION_KEY)
const reviewsVoted = useStorage<string[]>('review-votes', [], storage)

/**
 * We perform optimistic update.
 * We immediately modify the UI
 * We store the previous values and reset them if ever the API call fails
 */
async function vote(newVoteStatus: 'up' | 'unvoted') {
  const previousVoteStatus = voteStatus.value
  const previousReviewsVoted = [...reviewsVoted.value]

  voteStatus.value = newVoteStatus

  if (newVoteStatus === 'up') {
    reviewsVoted.value.push(reviewId)
  } else {
    reviewsVoted.value = reviewsVoted.value.filter((id) => id !== reviewId)
  }

  try {
    await $httpFetch('/v1/products/reviews/:uuid/vote', {
      pathParams: { uuid: reviewId },
      method: newVoteStatus === 'up' ? 'PUT' : 'DELETE',
    })
  } catch {
    voteStatus.value = previousVoteStatus
    reviewsVoted.value = previousReviewsVoted
    openErrorToast()
  }
}

onMounted(() => {
  if (reviewsVoted.value.includes(reviewId)) {
    hadPreviouslyVotedForReview = true
    voteStatus.value = 'up'
  }
})
</script>

<style scoped>
/**
* upAndDownIn and upAndDownOut are the same animations applied to the thumbs up icon.
* We use 2 different animations with the same values to apply them when voting and un-voting.
* If we used the same animation, nothing would happen when un-voting.
* The same applies to fontSizeChangeIn and fontSizeChangeOut.
*/
@keyframes upAndDownIn {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes upAndDownOut {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes fontSizeChangeIn {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes fontSizeChangeOut {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.iconAnimationIn {
  animation: upAndDownIn 0.5s;
}

.iconAnimationOut {
  animation: upAndDownOut 0.5s;
}

.voted {
  @apply bg-static-success-low  border-static-success-mid text-static-success-hi;
  transition: background-color 1000ms linear;
  transition: text-color 1000ms linear;
}

.textAnimationIn {
  animation: fontSizeChangeIn 0.5s;
}

.textAnimationOut {
  animation: fontSizeChangeOut 0.5s;
}
</style>
